/* Hook de i18n — carrega JSON do idioma ativo — 5 idiomas (PT/ES/IT/FR/EN) */
import { useState, useEffect, useCallback, useRef } from 'react';
import type { Locale } from '../types';

/** Chave no localStorage para persistir idioma */
const LOCALE_KEY = 'drpera-locale';
const DEFAULT_LOCALE: Locale = 'pt-BR';

/** Cache de session pra evitar fetch /api/geo múltiplas vezes na mesma sessão. */
const GEO_SESSION_KEY = 'drpera-ia-geo-locale-v1';

/** API base (mesmo padrão de useVoicePairing). */
const API_BASE =
  import.meta.env.VITE_API_BASE ?? 'https://drpera-api.luperazolo.workers.dev';

/** Lista de locales válidos suportados pelo app. */
const SUPPORTED_LOCALES: ReadonlySet<Locale> = new Set<Locale>([
  'pt-BR',
  'es',
  'it',
  'fr',
  'en',
]);

/**
 * Detecta locale via geo backend (briefing 22/05/2026).
 * Cache em sessionStorage pra evitar fetch repetido na mesma sessão.
 *
 * Falha aberta: se backend indisponível, retorna DEFAULT_LOCALE.
 * Locale só é considerado válido se está em SUPPORTED_LOCALES.
 *
 * NOTA: NÃO grava no localStorage. Geo é "guess" — só persiste se médico
 * mudar manualmente via LangSwitch (preserva intenção explícita).
 */
async function detectLocaleFromGeo(): Promise<Locale> {
  // Cache de sessão.
  try {
    const cached = sessionStorage.getItem(GEO_SESSION_KEY);
    if (cached && SUPPORTED_LOCALES.has(cached as Locale)) {
      return cached as Locale;
    }
  } catch {
    /* sessionStorage indisponível — segue pra fetch */
  }

  try {
    const res = await fetch(`${API_BASE}/api/geo`, {
      method: 'GET',
      // Sem credentials — endpoint público, sem cookies.
    });
    if (!res.ok) return DEFAULT_LOCALE;
    const body = (await res.json()) as { locale_hint?: string };
    const hint = body.locale_hint;
    const locale: Locale = SUPPORTED_LOCALES.has(hint as Locale)
      ? (hint as Locale)
      : DEFAULT_LOCALE;
    try {
      sessionStorage.setItem(GEO_SESSION_KEY, locale);
    } catch {
      /* ignore */
    }
    return locale;
  } catch {
    return DEFAULT_LOCALE;
  }
}

/** Tipo recursivo para valores de tradução */
type TranslationValue = string | Record<string, TranslationValue>;
type TranslationMap = Record<string, TranslationValue>;

/** Cache em memória para evitar reloads desnecessários */
const translationCache: Partial<Record<Locale, TranslationMap>> = {};

/** Carrega JSON de tradução do locale */
async function loadTranslations(locale: Locale): Promise<TranslationMap> {
  if (translationCache[locale]) return translationCache[locale]!;

  // Import dinâmico — Vite trata como chunk separado por locale
  let data: TranslationMap;
  switch (locale) {
    case 'es':
      data = (await import('../i18n/es.json')).default as TranslationMap;
      break;
    case 'it':
      data = (await import('../i18n/it.json')).default as TranslationMap;
      break;
    case 'fr':
      data = (await import('../i18n/fr.json')).default as TranslationMap;
      break;
    case 'en':
      data = (await import('../i18n/en.json')).default as TranslationMap;
      break;
    default:
      data = (await import('../i18n/pt-BR.json')).default as TranslationMap;
  }

  translationCache[locale] = data;
  return data;
}

/** Resolve caminho de ponto em mapa de tradução, ex: "login.cta.requestCode"
 *  Suporta:
 *  - Chave plana com pontos: `map["paywall.title.trial"] = "..."`
 *  - Chave aninhada: `map.paywall.title.trial = "..."`
 *  Tenta plana primeiro (compat com merges de staging), depois aninhada.
 */
function resolvePath(map: TranslationMap, path: string): string {
  // 1) Chave plana — usado quando i18n vem mergeado com chaves dot-separated top-level
  const flat = map[path];
  if (typeof flat === 'string') return flat;

  // 2) Chave aninhada — formato canônico
  const parts = path.split('.');
  let current: TranslationValue = map;
  for (const part of parts) {
    if (typeof current !== 'object' || current === null || !(part in current)) {
      return path; // fallback: retorna a chave se não encontrado
    }
    current = (current as Record<string, TranslationValue>)[part];
  }
  return typeof current === 'string' ? current : path;
}

/** Interface retornada pelo hook */
export interface UseTranslationsReturn {
  /** Função de tradução — aceita chave de ponto e vars de interpolação */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Idioma ativo */
  locale: Locale;
  /** Altera o idioma e persiste no localStorage */
  setLocale: (locale: Locale) => void;
  /** true enquanto carrega JSON */
  loadingI18n: boolean;
}

export function useTranslations(): UseTranslationsReturn {
  // Idioma inicial: usa localStorage se já tem escolha manual persistida;
  // senão começa com DEFAULT_LOCALE temporariamente e dispara geo detect.
  const storedLocale = (localStorage.getItem(LOCALE_KEY) as Locale | null);
  const [locale, setLocaleState] = useState<Locale>(storedLocale ?? DEFAULT_LOCALE);
  const [translations, setTranslations] = useState<TranslationMap>({});
  const [loadingI18n, setLoadingI18n] = useState(true);

  // Geo auto-detect (briefing 22/05/2026) — só roda se não há escolha manual.
  // Ref guard pra rodar 1× (StrictMode dispara useEffect 2× em dev).
  const geoDetectedRef = useRef(storedLocale !== null);
  useEffect(() => {
    if (geoDetectedRef.current) return;
    geoDetectedRef.current = true;
    void detectLocaleFromGeo().then((geoLocale) => {
      if (geoLocale !== DEFAULT_LOCALE) {
        // Geo retornou algo diferente do default — adota.
        // NÃO grava em localStorage (só manual choice persiste).
        setLocaleState(geoLocale);
      }
    });
  }, []);

  useEffect(() => {
    setLoadingI18n(true);
    loadTranslations(locale).then((data) => {
      setTranslations(data);
      setLoadingI18n(false);
    });
  }, [locale]);

  const setLocale = useCallback((newLocale: Locale) => {
    localStorage.setItem(LOCALE_KEY, newLocale);
    setLocaleState(newLocale);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string => {
      let text = resolvePath(translations, key);
      // Interpolação simples: {variavel}
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        });
      }
      return text;
    },
    [translations],
  );

  return { t, locale, setLocale, loadingI18n };
}
